import { useCallback, useEffect, useState } from "react";
import type { AdminFeedPost, FeedComment } from "@gooner/shared";
import { adminApi, api } from "../lib/api";
import { Button, cx } from "../control/ui/primitives";
import { ConfirmDialog } from "../control/components/ConfirmDialog";

/**
 * The Feed's moderation: the latest hundred posts, pulled ones included (a moderator
 * should see what was pulled and by whom), each with its thread on demand. Two acts —
 * pull a post or a comment, and end the account behind a post. An upload posted here
 * is cleared or rejected in the WALL queue, which is where its review row lives.
 */
export function FeedAdmin() {
  const [posts, setPosts] = useState<AdminFeedPost[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [threads, setThreads] = useState<Record<string, FeedComment[]>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [ban, setBan] = useState<AdminFeedPost | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    adminApi
      .feedPosts()
      .then((r) => setPosts(r.posts))
      .catch((e) => setError(e instanceof Error ? e.message : "Couldn't load the Feed"));
  }, []);
  useEffect(load, [load]);

  const toggleThread = (id: string) => {
    if (open === id) {
      setOpen(null);
      return;
    }
    setOpen(id);
    if (!threads[id]) {
      api
        .feedComments(id)
        .then((r) => setThreads((t) => ({ ...t, [id]: r.comments })))
        .catch(() => setThreads((t) => ({ ...t, [id]: [] })));
    }
  };
  const pullPost = async (p: AdminFeedPost) => {
    setBusy(p.id);
    try {
      await adminApi.feedPostDelete(p.id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't pull that post");
    } finally {
      setBusy(null);
    }
  };
  const pullComment = async (postId: string, c: FeedComment) => {
    setBusy(c.id);
    try {
      await adminApi.feedCommentDelete(c.id);
      setThreads((t) => ({ ...t, [postId]: (t[postId] ?? []).filter((x) => x.id !== c.id && x.parentId !== c.id) }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't pull that comment");
    } finally {
      setBusy(null);
    }
  };
  const banUser = async (p: AdminFeedPost) => {
    setBan(null);
    setBusy(p.id);
    try {
      await adminApi.userBan(p.userId);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't ban that account");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-zinc-100">The Feed</h2>
        <Button size="sm" onClick={load}>
          Refresh
        </Button>
      </div>
      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      {posts === null ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : posts.length === 0 ? (
        <p className="text-sm text-zinc-500">Nothing posted yet.</p>
      ) : (
        <ul className="space-y-2">
          {posts.map((p) => (
            <li key={p.id} className={cx("rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-2.5", p.deletedAt !== null && "opacity-60")}>
              <div className="flex items-center gap-3">
                {p.item ? <Thumb item={p.item} /> : <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded bg-[var(--color-panel-2)] text-[10px] text-zinc-400">collection</div>}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-zinc-100">
                    @{p.author.username}
                    <span className="ml-2 font-normal text-zinc-500">
                      {p.collection ? `collection · ${p.collection.name}` : p.item?.kind}
                      {p.pending ? " · in review" : ""}
                      {p.deletedAt ? ` · pulled by ${p.deletedBy}` : ""}
                      {" · "}
                      {p.crowns} crown{p.crowns === 1 ? "" : "s"} · {p.comments} comment{p.comments === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="truncate text-xs text-zinc-400">{p.caption || <span className="text-zinc-600">(no caption)</span>}</div>
                  <div className="text-[11px] text-zinc-600">{new Date(p.createdAt).toLocaleString()}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button size="sm" onClick={() => toggleThread(p.id)}>
                    {open === p.id ? "Hide thread" : "Thread"}
                  </Button>
                  {!p.deletedAt ? (
                    <Button size="sm" variant="danger" disabled={busy === p.id} onClick={() => void pullPost(p)}>
                      Pull
                    </Button>
                  ) : null}
                  <Button size="sm" variant="danger" disabled={busy === p.id} onClick={() => setBan(p)}>
                    Ban account
                  </Button>
                </div>
              </div>
              {open === p.id ? (
                <ul className="mt-2 space-y-1 border-t border-[var(--color-edge)] pt-2">
                  {(threads[p.id] ?? []).length === 0 ? (
                    <li className="text-xs text-zinc-500">{threads[p.id] ? "No comments." : "Loading…"}</li>
                  ) : (
                    threads[p.id]!.map((c) => (
                      <li key={c.id} className={cx("flex items-start gap-2 text-xs", c.parentId && "ml-6")}>
                        <span className="shrink-0 font-medium text-zinc-200">@{c.author.username}</span>
                        <span className="min-w-0 flex-1 text-zinc-300">{c.body}</span>
                        <button
                          type="button"
                          disabled={busy === c.id}
                          onClick={() => void pullComment(p.id, c)}
                          className="shrink-0 text-red-400 hover:underline"
                        >
                          pull
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={ban !== null}
        title={`Ban @${ban?.author.username}?`}
        message="Their account ends now: every session dies, and their posts and comments leave the Feed. Undo from the Users tab."
        confirmLabel="Ban"
        onConfirm={() => ban && void banUser(ban)}
        onCancel={() => setBan(null)}
      />
    </div>
  );
}

/** The media itself — a moderator has to SEE it (the wall queue's thumb). */
function Thumb({ item }: { item: NonNullable<AdminFeedPost["item"]> }) {
  return item.kind === "image" ? (
    <img src={item.posterUrl ?? item.url} alt="" className="h-14 w-20 shrink-0 rounded object-cover" />
  ) : (
    <video
      src={item.url}
      poster={item.posterUrl}
      className="h-14 w-20 shrink-0 rounded object-cover"
      muted
      loop
      playsInline
      preload="metadata"
      onMouseEnter={(e) => void e.currentTarget.play().catch(() => undefined)}
      onMouseLeave={(e) => e.currentTarget.pause()}
    />
  );
}
